import { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import Asterisk from '../../../components/Asterisk'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../app/hooks'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../app/store'
import { toast } from 'react-toastify'
import {
  GET_OPD_VITALS_ENDPOINT,
  POST_OPD_VITALS_ENDPOINT,
} from '../../../utils/Endpoints'
import {
  capitalize,
  FORMIK_SUBMISSION_CONST,
  FORMIK_VALIDATIONS,
  getVisitType,
  toastMessages,
  VITALS_TEXT_FIELDS,
  VITALS_TOGGLE_FIELDS,
} from './constants'
import {
  isInitialLoadForOPDForms,
  isVitalsFormSubmitted,
  selectVitals,
  setIsVitalsFormSubmit,
  setLoading,
  updateVitals,
} from './opdFormsSlice'
import {
  opdFormDataFetchApi,
  opdFormDataSaveApi,
} from '../../../services/OpdFormsSTTService'

const styles = (theme: Theme) => ({
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      width: '350px',
      padding: '4px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
    width: '160px !important',
  },
  fieldLabel: {
    textAlign: 'right',
    fontSize: '12px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '228px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '250px',
    },
  },
  buttonStyle: {
    fontFamily: 'Open Sans, sans-serif',
    margin: '2px',
    justifyContent: 'center',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    textTransform: 'capitalize',
    width: '147px',
  },
  selectedButton: {
    backgroundColor: '#4682b4 !important',
    color: '#f1f3f5 !important',
    fontWeight: 600,
    border: '1px solid #4682b4 !important',
  },
  unselectedButton: {
    color: '#6c757d !important',
    border: '1px solid #ddd !important',
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '78%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '67%',
    },
  },
})

const Vitals = (intentType: any) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const visitType = getVisitType(intentType.intent)
  const vitals: any = useAppSelector(selectVitals)
  const dispatch = useDispatch<AppDispatch>()
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isVitalsFormSubmitted)

  const initialValues = {
    pulse: vitals?.pulse || '',
    temperature: vitals?.temperature || '',
    respiratoryRate: vitals?.respiratoryRate || '',
    spo2: vitals?.spo2 || '',
    systolic: vitals?.systolic || '',
    diastolic: vitals?.diastolic || '',
    posture: vitals?.posture || '',
  }

  const validationSchema = Yup?.object({
    pulse: Yup?.number()?.required(FORMIK_VALIDATIONS?.Pulse),
    temperature: Yup?.number()?.required(FORMIK_VALIDATIONS?.Temperature),
    respiratoryRate: Yup?.number()?.required(
      FORMIK_VALIDATIONS?.RespiratoryRate
    ),
    spo2: Yup?.number()?.required(FORMIK_VALIDATIONS?.SPO2),
    systolic: Yup?.number()?.required(FORMIK_VALIDATIONS?.Systolic),
    diastolic: Yup?.number()?.required(FORMIK_VALIDATIONS?.Diastolic),
    posture: Yup?.string(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleVitalsSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleVitalsSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(POST_OPD_VITALS_ENDPOINT, payload)

    if (response?.success) {
      dispatch(setIsVitalsFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }

    setSubmitting(false)
  }

  const getVitals = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getVitalsUrl = `${GET_OPD_VITALS_ENDPOINT}${id}&visitType=opd/${visitType}`
    const response = await opdFormDataFetchApi(getVitalsUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateVitals({
          fhirId: fhirRecordId,
          pulse: result?.pulse,
          temperature: result?.temparature || result?.temperature,
          respiratoryRate: result?.resprate,
          spo2: result?.spo2,
          systolic: result?.systolic,
          diastolic: result?.diastolic,
          posture: capitalize(result?.posture),
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }

    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getVitals()
  }, [getVitals])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      {VITALS_TEXT_FIELDS?.filter((row) =>
        row?.validVisitTypes?.includes(visitType)
      )?.map(({ name, label }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'100%'}
          key={name}
          flexWrap={'wrap'}
          gap={2}
        >
          <Typography className={classes?.fieldLabel}>
            {label}
            <Asterisk />:
          </Typography>
          <Box>
            <TextField
              size='small'
              type='number'
              name={name}
              inputProps={{ 'data-testid': name }}
              value={formik?.values[name as keyof typeof formik.values] || ''}
              fullWidth
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              error={
                formik?.touched[name as keyof typeof formik.values] &&
                Boolean(formik?.errors[name as keyof typeof formik.errors])
              }
              helperText={
                formik?.touched[name as keyof typeof formik.values] &&
                (formik?.errors[name as keyof typeof formik.errors] as string)
              }
              className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
            />
          </Box>
        </Box>
      ))}

      {VITALS_TOGGLE_FIELDS?.filter((row) =>
        row?.validVisitTypes?.includes(visitType)
      )?.map(({ name, label, options }) => (
        <Box
          key={name}
          display='flex'
          justifyContent='start'
          alignItems='center'
          width='100%'
          flexWrap='wrap'
          gap={2}
        >
          <Typography className={classes?.fieldLabel}>
            {label}
            <Asterisk />:
          </Typography>

          <Box display='flex' flexWrap='wrap' gap={'2px'}>
            {options?.map((option) => {
              const isSelected =
                formik?.values[name as keyof typeof formik.values] === option

              const buttonClassName = `${classes?.buttonStyle} ${
                isSelected ? classes?.selectedButton : classes?.unselectedButton
              }`

              return (
                <Button
                  size='small'
                  key={option}
                  onClick={() => formik?.setFieldValue(name, option)}
                  className={buttonClassName}
                >
                  {option}
                </Button>
              )
            })}
          </Box>
        </Box>
      ))}

      <Box
        display='flex'
        justifyContent='flex-end'
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          disabled={formik?.isSubmitting}
        >
          {formik?.isSubmitting
            ? FORMIK_SUBMISSION_CONST.Submitting
            : FORMIK_SUBMISSION_CONST.Submit}
        </Button>
      </Box>
    </Box>
  )
}

export default Vitals
